(function () {
  'use strict';

  const DEBUG_FLAG_KEY = 'upsell_debug_enabled';

  const SELECTORS = {
    upsellHolder: '[data-upsell-products]',
    upsellMeta: '[data-cart-upsell-meta]',
    quickAddHolder: '[data-quick-add-holder]',
    upsellWidget: '[data-upsell-widget]',
    collapsibleBody: '[data-collapsible-body]',
    upsellPrev: '[data-upsell-prev]',
    upsellNext: '[data-upsell-next]'
  };

  function isDebugEnabled() {
    // Debug UI intentionally disabled in production storefront.
    window.sessionStorage.removeItem(DEBUG_FLAG_KEY);
    return false;
  }

  function safeStringify(value) {
    try {
      return JSON.stringify(value, null, 2);
    } catch (error) {
      return String(value);
    }
  }

  function getDebugPanel() {
    let panel = document.querySelector('[data-upsell-debug-panel]');
    if (panel) return panel;

    panel = document.createElement('details');
    panel.setAttribute('data-upsell-debug-panel', '');
    panel.style.position = 'fixed';
    panel.style.right = '12px';
    panel.style.bottom = '12px';
    panel.style.width = 'min(560px, calc(100vw - 24px))';
    panel.style.maxHeight = '65vh';
    panel.style.overflow = 'auto';
    panel.style.zIndex = '99999';
    panel.style.background = '#111';
    panel.style.color = '#eee';
    panel.style.border = '1px solid #444';
    panel.style.borderRadius = '6px';
    panel.style.padding = '8px';
    panel.style.fontSize = '12px';
    panel.style.fontFamily = 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace';
    panel.open = true;

    const summary = document.createElement('summary');
    summary.textContent = 'Upsell debug';
    summary.style.cursor = 'pointer';
    summary.style.marginBottom = '8px';
    panel.appendChild(summary);

    const actions = document.createElement('div');
    actions.style.display = 'flex';
    actions.style.gap = '8px';
    actions.style.marginBottom = '8px';

    const copyButton = document.createElement('button');
    copyButton.type = 'button';
    copyButton.textContent = 'Copy debug';
    copyButton.style.padding = '6px 10px';
    copyButton.style.cursor = 'pointer';
    copyButton.addEventListener('click', async () => {
      const output = panel.querySelector('[data-upsell-debug-output]');
      if (!output) return;
      try {
        await navigator.clipboard.writeText(output.value);
        copyButton.textContent = 'Copied';
        setTimeout(() => {
          copyButton.textContent = 'Copy debug';
        }, 1200);
      } catch (error) {
        copyButton.textContent = 'Copy failed';
      }
    });
    actions.appendChild(copyButton);

    const closeButton = document.createElement('button');
    closeButton.type = 'button';
    closeButton.textContent = 'Disable debug';
    closeButton.style.padding = '6px 10px';
    closeButton.style.cursor = 'pointer';
    closeButton.addEventListener('click', () => {
      window.sessionStorage.removeItem(DEBUG_FLAG_KEY);
      panel.remove();
    });
    actions.appendChild(closeButton);
    panel.appendChild(actions);

    const output = document.createElement('textarea');
    output.setAttribute('data-upsell-debug-output', '');
    output.readOnly = true;
    output.style.width = '100%';
    output.style.minHeight = '280px';
    output.style.background = '#0b0b0b';
    output.style.color = '#d9f99d';
    output.style.border = '1px solid #333';
    output.style.padding = '8px';
    output.style.resize = 'vertical';
    panel.appendChild(output);

    document.body.appendChild(panel);
    return panel;
  }

  function writeDebug(payload) {
    if (!isDebugEnabled()) return;
    const panel = getDebugPanel();
    const output = panel.querySelector('[data-upsell-debug-output]');
    if (!output) return;

    output.value = safeStringify(payload);
  }

  function syncUpsellWidgetVisibility(holder, debug) {
    const widget = holder.closest(SELECTORS.upsellWidget);
    const itemCount = holder.querySelectorAll(SELECTORS.quickAddHolder).length;
    const hasItems = itemCount > 0;

    if (!widget) {
      if (debug) debug.notes.push('No upsell widget wrapper found.');
      return;
    }

    widget.classList.toggle('is-hidden', !hasItems);

    if (hasItems && widget.hasAttribute('data-upsell-auto-open') && !widget.hasAttribute('open')) {
      widget.setAttribute('open', 'true');
      const body = widget.querySelector(SELECTORS.collapsibleBody);
      if (body) {
        body.style.height = 'auto';
      }
    }

    if (debug) {
      debug.notes.push(`Widget sync: hasItems=${hasItems}, itemCount=${itemCount}, hiddenClass=${widget.classList.contains('is-hidden')}`);
    }
  }

  function initUpsellSlider(holder, debug) {
    const track = holder;
    const widget = track.closest(SELECTORS.upsellWidget);
    if (!widget) return;

    const prevButton = widget.querySelector(SELECTORS.upsellPrev);
    const nextButton = widget.querySelector(SELECTORS.upsellNext);
    if (!prevButton || !nextButton) return;

    const updateButtons = () => {
      const maxScroll = Math.max(0, track.scrollWidth - track.clientWidth);
      const canScroll = maxScroll > 4;
      const isAtStart = track.scrollLeft <= 2;
      const isAtEnd = track.scrollLeft >= maxScroll - 2;

      prevButton.style.display = canScroll ? 'inline-flex' : 'none';
      nextButton.style.display = canScroll ? 'inline-flex' : 'none';
      prevButton.disabled = !canScroll || isAtStart;
      nextButton.disabled = !canScroll || isAtEnd;
    };

    if (!track.hasAttribute('data-upsell-slider-init')) {
      track.setAttribute('data-upsell-slider-init', 'true');
      prevButton.addEventListener('click', () => {
        track.scrollBy({ left: -track.clientWidth, behavior: 'smooth' });
      });
      nextButton.addEventListener('click', () => {
        track.scrollBy({ left: track.clientWidth, behavior: 'smooth' });
      });
      track.addEventListener('scroll', () => window.requestAnimationFrame(updateButtons), { passive: true });
      window.addEventListener('resize', () => window.requestAnimationFrame(updateButtons));
    }

    window.requestAnimationFrame(updateButtons);
    if (debug) debug.notes.push('Upsell slider initialized/refreshed.');
  }

  function getCartProductIdsFromMeta(metaEl) {
    const csv = metaEl?.getAttribute('data-cart-product-ids') || '';
    return csv
      .split(',')
      .map((id) => id.trim())
      .filter(Boolean);
  }

  function getIntents(source) {
    if (source === 'related') return ['related'];
    if (source === 'complementary') return ['complementary'];
    return ['complementary', 'related'];
  }

  function buildRecommendationsJsonUrl(productId, intent, limit) {
    const route = window.theme?.routes?.product_recommendations_url || '/recommendations/products';
    const basePath = route.replace(/\/+$/, '');
    return `${basePath}.json?product_id=${productId}&intent=${intent}&limit=${limit}`;
  }

  async function fetchRecommendations(productId, intent, limit) {
    if (!productId) {
      return { products: [], url: '', status: 'missing-input' };
    }

    const url = buildRecommendationsJsonUrl(productId, intent, limit);
    const response = await fetch(url);
    if (!response.ok) {
      return { products: [], url, status: response.status };
    }

    const data = await response.json();
    return {
      products: Array.isArray(data?.products) ? data.products : [],
      url,
      status: response.status
    };
  }

  async function fetchUpsellCard(productUrl) {
    if (!productUrl) {
      return { html: '', url: '', status: 'missing-product-url' };
    }

    const url = `${productUrl}${productUrl.includes('?') ? '&' : '?'}section_id=api-upsell-product`;
    const response = await fetch(url);
    if (!response.ok) {
      return { html: '', url, status: response.status };
    }

    const html = await response.text();
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const content = doc.querySelector('[data-api-content]');
    return {
      html: content ? content.innerHTML : '',
      url,
      status: response.status
    };
  }

  function pruneInvalidUpsells(container, cartProductIds, seenProductIds) {
    const cards = container.querySelectorAll(SELECTORS.quickAddHolder);

    cards.forEach((card) => {
      const productId = card.getAttribute('data-quick-add-holder');
      if (!productId || cartProductIds.includes(productId) || seenProductIds.has(productId)) {
        const quickAddProduct = card.closest('quick-add-product');
        if (quickAddProduct) quickAddProduct.remove();
        return;
      }

      seenProductIds.add(productId);
    });
  }

  async function hydrateUpsellHolder(holder) {
    const debug = {
      timestamp: new Date().toISOString(),
      source: null,
      firstProductId: null,
      cartProductIds: [],
      hasUpsellsAlready: false,
      attempts: [],
      finalUpsellCount: 0,
      notes: []
    };

    const metaEl = holder.querySelector(SELECTORS.upsellMeta);
    if (!metaEl) {
      debug.notes.push('No [data-cart-upsell-meta] found inside upsell holder.');
      writeDebug(debug);
      return;
    }

    const firstProductId = metaEl.getAttribute('data-first-cart-product-id');
    const source = metaEl.getAttribute('data-recommendation-source') || 'auto';
    const intents = getIntents(source);
    const cartProductIds = getCartProductIdsFromMeta(metaEl);
    const seenProductIds = new Set();

    debug.source = source;
    debug.firstProductId = firstProductId;
    debug.cartProductIds = cartProductIds;

    const hasUpsellsAlready = holder.querySelector(SELECTORS.quickAddHolder);
    debug.hasUpsellsAlready = Boolean(hasUpsellsAlready);
    syncUpsellWidgetVisibility(holder, debug);
    initUpsellSlider(holder, debug);
    if (hasUpsellsAlready) {
      debug.notes.push('Upsells already present from server-side render, API fallback skipped.');
      debug.finalUpsellCount = holder.querySelectorAll(SELECTORS.quickAddHolder).length;
      syncUpsellWidgetVisibility(holder, debug);
      initUpsellSlider(holder, debug);
      writeDebug(debug);
      return;
    }

    for (const intent of intents) {
      let result;
      try {
        result = await fetchRecommendations(firstProductId, intent, 6);
      } catch (error) {
        debug.attempts.push({
          intent,
          error: String(error)
        });
        continue;
      }

      debug.attempts.push({
        intent,
        url: result.url,
        status: result.status,
        recommendationsCount: result.products.length
      });

      if (!result.products.length) continue;

      let insertedForIntent = 0;
      const fetchedCards = [];

      for (const product of result.products) {
        const productId = String(product?.id || '');
        const productUrl = product?.url || (product?.handle ? `/products/${product.handle}` : '');

        if (!productId || !productUrl) continue;
        if (cartProductIds.includes(productId) || seenProductIds.has(productId)) continue;

        let cardResult;
        try {
          cardResult = await fetchUpsellCard(productUrl);
        } catch (error) {
          fetchedCards.push({
            productId,
            productUrl,
            error: String(error)
          });
          continue;
        }

        fetchedCards.push({
          productId,
          productUrl,
          url: cardResult.url,
          status: cardResult.status,
          htmlLength: cardResult.html.length
        });

        if (cardResult.html.trim() !== '') {
          holder.insertAdjacentHTML('beforeend', cardResult.html);
          seenProductIds.add(productId);
          insertedForIntent += 1;
        }
      }
      debug.attempts[debug.attempts.length - 1].cards = fetchedCards;
      debug.attempts[debug.attempts.length - 1].inserted = insertedForIntent;

      // For auto mode, stop after first intent that yields products.
      if (source === 'auto' && seenProductIds.size > 0) break;
    }

    debug.finalUpsellCount = holder.querySelectorAll(SELECTORS.quickAddHolder).length;
    if (debug.finalUpsellCount === 0) {
      debug.notes.push('No upsell products rendered after fallback.');
    }
    syncUpsellWidgetVisibility(holder, debug);
    initUpsellSlider(holder, debug);
    writeDebug(debug);

    // Let existing cart JS re-check accordion visibility only when new items were added.
    if (seenProductIds.size > 0) {
      document.dispatchEvent(new CustomEvent('theme:cart:change', { bubbles: true }));
    }
  }

  function initCartUpsellRecommendations() {
    const holders = document.querySelectorAll(SELECTORS.upsellHolder);
    if (!holders.length) return;
    holders.forEach((holder) => hydrateUpsellHolder(holder));
  }

  document.addEventListener('DOMContentLoaded', initCartUpsellRecommendations);
  document.addEventListener('theme:cart:change', () => {
    // Wait one tick because cart drawer DOM can be replaced on update.
    window.requestAnimationFrame(initCartUpsellRecommendations);
  });
})();
